import { HttpClient } from '@angular/common/http';
import { Component, HostListener, Input, LOCALE_ID, OnInit, ViewEncapsulation, inject, signal } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { VersionService } from '@app/services/version.service';
import { LoadingDotsComponent } from '@app/chat/components/loading-dots/loading-dots.component';
import { UpdateHintComponent } from '@app/version/update-hint.component';
import { ScrollContainerComponent } from '../scroll-container/scroll-container.component';
import releaseNotes from '@assets/release-notes/release-notes.json';
import { TranslocoPipe } from '@jsverse/transloco';
import * as Sentry from '@sentry/angular';
import { MarkdownModule } from 'ngx-markdown';
import { Button } from 'primeng/button';

export interface ReleaseNote {
  date: Date;
  text: string;
}

@Component({
  selector: 'app-release-notes',
  imports: [TranslocoPipe, MarkdownModule, LoadingDotsComponent, ScrollContainerComponent, Button, UpdateHintComponent],
  providers: [{ provide: LOCALE_ID, useValue: 'de' }],
  templateUrl: './release-notes.component.html',
  styleUrl: './release-notes.component.scss',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
})
export class ReleaseNotesComponent implements OnInit {
  versionService = inject(VersionService);
  @Input() data: any;
  @Input() close: ((event?: any) => void) | undefined;

  http = inject(HttpClient);

  sortedReleaseNotes: ReleaseNote[] = [];
  loading = signal(true);

  static ASSETS_PATH = '/assets/release-notes/';

  async ngOnInit() {
    try {
      // read in markdown files
      const notes = await Promise.all(
        releaseNotes.map(async (nodeFile) => {
          const content = await lastValueFrom(
            this.http.get(`${ReleaseNotesComponent.ASSETS_PATH}${nodeFile.name}`, {
              responseType: 'text',
            }),
          );

          if (nodeFile.name.endsWith('.md')) {
            const dateString = nodeFile.name.replace('Z.md', '');
            return {
              date: new Date(dateString),
              text: content,
            };
          } else {
            // ignore the json file
            return undefined;
          }
        }),
      );

      // sort notes by date
      this.sortedReleaseNotes = notes.filter((note): note is ReleaseNote => note !== undefined).sort((a, b) => b.date.getTime() - a.date.getTime());

      // The red dot should disappear a little time after opening the release notes,
      // to give the user the chance to see it and understand that there is an update
      setTimeout(() => this.versionService.setUpdateSeen(), 1000);

      this.loading.set(false);
    } catch (error) {
      console.error('Error loading release notes:', error);
      Sentry.captureException(error, {
        tags: {
          location: 'ReleaseNotesComponent',
          feature: 'ReleaseNotes',
          message: 'Error loading release notes',
        },
      });
    }
  }

  public closeDialog() {
    this.close?.();
  }

  @HostListener('document:keydown.escape')
  public onKeydownHandler() {
    this.closeDialog();
  }
}
